import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, convertFromMXN, convertToMXN } from '../../utils/formatters';
import { DollarSign, Target, Calendar, Percent, ChevronRight, ChevronLeft, Plus, Minus, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';

import { getLanguageContent, getLanguageFromCurrency } from '../../data/languages';

interface ReinvestmentCentralDataProps {
  onNext: () => void;
  onPrevious?: () => void;
}

const ReinvestmentCentralData: React.FC<ReinvestmentCentralDataProps> = ({ onNext, onPrevious }) => {
  const { investment, updateInvestment } = useCalculator();
  
  // Get current language content
  const currentLanguage = investment.language || getLanguageFromCurrency(investment.currencyFormat);
  const content = getLanguageContent(currentLanguage);

  // Handlers for increment/decrement buttons
  const handleIncrementCertificates = () => {
    if (investment.initialCertificates < 20) {
      updateInvestment({ initialCertificates: investment.initialCertificates + 1 });
    }
  };

  const handleDecrementCertificates = () => {
    if (investment.initialCertificates > 1) {
      updateInvestment({ initialCertificates: investment.initialCertificates - 1 });
    }
  };

  const handleIncrementPrice = () => {
    const increment = convertToMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
    const maxPrice = convertToMXN(300000, 'MXN', investment.exchangeRate, investment.exchangeRateEUR);
    
    if (investment.certificateBasePrice < maxPrice) {
      updateInvestment({ certificateBasePrice: investment.certificateBasePrice + increment });
    }
  };

  const handleDecrementPrice = () => {
    const decrement = convertToMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
    const minPrice = convertToMXN(200000, 'MXN', investment.exchangeRate, investment.exchangeRateEUR);
    
    if (investment.certificateBasePrice > minPrice) {
      updateInvestment({ certificateBasePrice: investment.certificateBasePrice - decrement });
    }
  };

  const handleIncrementYears = () => {
    if (investment.years < 30) {
      updateInvestment({ years: investment.years + 1 });
    }
  };

  const handleDecrementYears = () => {
    if (investment.years > 5) {
      updateInvestment({ years: investment.years - 1 });
    }
  };

  const handleIncrementProfit = () => {
    if (investment.annualProfit < 30) {
      updateInvestment({ annualProfit: investment.annualProfit + 1 });
    }
  };

  const handleDecrementProfit = () => {
    if (investment.annualProfit > 10) {
      updateInvestment({ annualProfit: investment.annualProfit - 1 });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Target className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{content.reinvestment.centralData?.title || 'Datos Centrales de Inversión'}</h2>
              <p className="text-blue-100">{content.reinvestment.centralData?.subtitle || 'Configura los parámetros principales de tu inversión con reinversión automática'}</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Initial Certificates */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Certificados Iniciales</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementCertificates}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.initialCertificates <= 1}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={investment.initialCertificates}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 1 && value <= 20) {
                      updateInvestment({ initialCertificates: value });
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                />
                <div className="text-sm text-blue-600">
                  certificados
                </div>
              </div>
              <button 
                onClick={handleIncrementCertificates}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.initialCertificates >= 20}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="1"
                max="20"
                step="1"
                value={investment.initialCertificates}
                onChange={(e) => updateInvestment({ initialCertificates: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg appearance-none cursor-pointer"
                title="Ajusta el número de certificados iniciales"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>1</span>
                <span>20</span>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl">
              <div className="text-xs text-gray-600 mb-1">Inversión inicial:</div>
              <div className="text-xl font-bold text-gray-900">
                {formatCurrency(convertFromMXN(investment.initialCertificates * investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
              </div>
            </div>
          </div>

          {/* Certificate Price and Horizon */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Certificate Price */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Precio del Certificado</h3>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {formatCurrency(convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button 
                    onClick={handleDecrementPrice}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.certificateBasePrice <= 200000}
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </button>
                  <input
                    type="range"
                    min={convertFromMXN(200000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                    max={convertFromMXN(300000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                    step={convertFromMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                    value={convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                    onChange={(e) => {
                      const displayValue = parseFloat(e.target.value);
                      const mxnValue = convertToMXN(displayValue, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
                      updateInvestment({ certificateBasePrice: mxnValue });
                    }}
                    className="flex-1 h-2 bg-gradient-to-r from-indigo-200 to-indigo-300 rounded-lg appearance-none cursor-pointer mx-2"
                  />
                  <button 
                    onClick={handleIncrementPrice}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.certificateBasePrice >= 300000}
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
              </div>
            </div>

            {/* Investment Horizon */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Horizonte de Inversión</h3>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {investment.years} años
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button 
                    onClick={handleDecrementYears}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.years <= 5}
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </button>
                  <input
                    type="range"
                    min="5"
                    max="30"
                    step="1"
                    value={investment.years}
                    onChange={(e) => updateInvestment({ years: parseInt(e.target.value) })}
                    className="flex-1 h-2 bg-gradient-to-r from-purple-200 to-purple-300 rounded-lg appearance-none cursor-pointer mx-2"
                  />
                  <button 
                    onClick={handleIncrementYears}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.years >= 30}
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Production Parameters - Compact */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Parámetros de Producción</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Production per Hectare - Compact */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{content.traditional.centralData.production.productionLabel}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="15000"
                    max="35000"
                    step="500"
                    value={investment.averageProductionPerHectare}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 15000 && value <= 35000) {
                        updateInvestment({ averageProductionPerHectare: value });
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <span className="text-gray-700 text-sm">
                    {currentLanguage === 'es' ? 'kg/ha' :
                     currentLanguage === 'en' ? 'kg/ha' :
                     'kg/ha'}
                  </span>
                </div>
              </div>
              
              {/* Price per Kg - Compact */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{content.traditional.centralData.production.priceLabel}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="20"
                    max="50"
                    step="1"
                    value={investment.averageSalePricePerKg}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 20 && value <= 50) {
                        updateInvestment({ averageSalePricePerKg: value });
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <span className="text-gray-700 text-sm">MXN/kg</span>
                </div>
              </div>
            </div>
            
            {/* Calculated Annual Profit Display */}
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">{content.traditional.centralData.production.calculatedProfit}</div>
              <div className="text-2xl font-bold text-green-700">
                {((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-green-600 mt-1">
                {investment.averageProductionPerHectare.toLocaleString()} kg/ha × ${investment.averageSalePricePerKg}/kg × 0.1 × 0.65
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                { label: content.traditional.centralData.production.scenarios.conservative, production: 25000, price: 30 },
                { label: content.traditional.centralData.production.scenarios.moderate, production: 30000, price: 35 },
                { label: content.traditional.centralData.production.scenarios.optimistic, production: 35000, price: 38 }
              ].map((scenario) => (
                <button
                  key={scenario.label}
                  onClick={() => updateInvestment({ 
                    averageProductionPerHectare: scenario.production,
                    averageSalePricePerKg: scenario.price
                  })}
                  className={`py-3 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    investment.averageProductionPerHectare === scenario.production && investment.averageSalePricePerKg === scenario.price
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {scenario.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Continue Button */}
          <div className="flex justify-end">
            {onPrevious && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onPrevious}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Anterior</span>
              </motion.button>
            )}
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNext}
              className={`px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 ${!onPrevious ? 'ml-auto' : ''}`}
            >
              <span>Continuar</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReinvestmentCentralData;